import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Phone, Mail, User, MessageSquare, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { submitLead, getGeoLocation, buildLeadPayload, getCountryCodeFromDialCode } from '../services/api';
import { validateEmail, validatePhone, validateName, validateRequired } from '../utils/validation';

const ContactModal = ({ isOpen, onClose, selectedPackage }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let validation = { isValid: true, error: '' };

    switch (field) {
      case 'name':
        validation = validateName(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhone(value, formData.countryCode);
        break;
      case 'description':
        validation = validateRequired(value, 'Description', 1);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: validation.error
    }));

    return validation.isValid;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Validate on change if field was already touched
    if (touched[field]) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      description: true
    });

    // Validate all fields
    const nameValid = validateField('name', formData.name);
    const emailValid = validateField('email', formData.email);
    const phoneValid = validateField('phone', formData.phone);
    const descValid = validateField('description', formData.description);

    if (!nameValid || !emailValid || !phoneValid || !descValid) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form before submitting',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Fetch geolocation data
      const geoData = await getGeoLocation();
      
      // Extract numeric dial code from countryCode
      const dialCode = formData.countryCode.replace('+', '');
      
      // Build complete payload
      const payload = buildLeadPayload({
        name: formData.name,
        email: formData.email,
        countryDialCode: dialCode,
        phoneNumber: formData.phone,
        description: formData.description,
        packageTitle: selectedPackage?.name,
        packageAmount: selectedPackage?.price,
        tmSignUpType: selectedPackage?.tmSignUpType
      }, geoData);

      // Submit lead
      const result = await submitLead(payload);

      if (result.success && result.statusCode === 200) {
        toast({
          title: 'Success!',
          description: result.message || 'Your request has been submitted successfully.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryCode: '+1',
          description: ''
        });
        setErrors({});
        setTouched({});
        
        // Close modal
        onClose();
        
        // Redirect to application page with customerId
        if (result.customerId) {
          window.location.href = `/thanks?customerid=${result.customerId}`;
        }
      } else {
        toast({
          title: 'Error',
          description: result.message || 'Something went wrong. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 gap-0 overflow-hidden max-h-[90vh]">
        <div className="grid md:grid-cols-2 h-full">
          {/* Left Side - Image */}
          <div className="relative hidden md:block">
            <img
              src="https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NjIzMDMyOTd8MA&ixlib=rb-4.1.0&q=85"
              alt="Professional Business & Legal Services"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            <div className="absolute bottom-8 left-8 right-8 text-white">
              <h3 className="text-2xl font-bold mb-3">Let's Get Started</h3>
              <p className="text-lg text-gray-100">
                Protect your brand and business with our expert services
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="relative p-8 bg-white flex flex-col">
            <div className="mb-6 pr-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Get in Touch</h2>
              <p className="text-base text-gray-600">Fill out the form and we'll contact you shortly</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                    className={`pl-10 py-5 ${
                      errors.name && touched.name
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary'
                    }`}
                  />
                </div>
                {errors.name && touched.name && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    className={`pl-10 py-5 ${
                      errors.email && touched.email
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary'
                    }`}
                  />
                </div>
                {errors.email && touched.email && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Phone with Country Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Number *
                </label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                    <SelectTrigger className="w-24 py-5 border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+1">🇺🇸 +1</SelectItem>
                      <SelectItem value="+44">🇬🇧 +44</SelectItem>
                      <SelectItem value="+91">🇮🇳 +91</SelectItem>
                      <SelectItem value="+92">🇵🇰 +92</SelectItem>
                      <SelectItem value="+86">🇨🇳 +86</SelectItem>
                      <SelectItem value="+81">🇯🇵 +81</SelectItem>
                      <SelectItem value="+49">🇩🇪 +49</SelectItem>
                      <SelectItem value="+33">🇫🇷 +33</SelectItem>
                      <SelectItem value="+61">🇦🇺 +61</SelectItem>
                      <SelectItem value="+971">🇦🇪 +971</SelectItem>
                      <SelectItem value="+966">🇸🇦 +966</SelectItem>
                      <SelectItem value="+971">🇦🇪 +971</SelectItem>
                      <SelectItem value="+65">🇸🇬 +65</SelectItem>
                      <SelectItem value="+60">🇲🇾 +60</SelectItem>
                      <SelectItem value="+63">🇵🇭 +63</SelectItem>
                      <SelectItem value="+234">🇳🇬 +234</SelectItem>
                      <SelectItem value="+27">🇿🇦 +27</SelectItem>
                      <SelectItem value="+52">🇲🇽 +52</SelectItem>
                      <SelectItem value="+55">🇧🇷 +55</SelectItem>
                      <SelectItem value="+34">🇪🇸 +34</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type="tel"
                      placeholder="123-456-7890"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        handleChange('phone', value);
                      }}
                      onBlur={() => handleBlur('phone')}
                      maxLength={10}
                      className={`pl-10 py-5 ${
                        errors.phone && touched.phone
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                          : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary'
                      }`}
                    />
                  </div>
                </div>
                {errors.phone && touched.phone && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.phone}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-gray-400" size={18} />
                  <Textarea
                    placeholder="Tell us about your needs..."
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    onBlur={() => handleBlur('description')}
                    className={`pl-10 pt-3 h-24 resize-none ${
                      errors.description && touched.description
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                        : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary'
                    }`}
                  />
                </div>
                {errors.description && touched.description && (
                  <div className="flex items-center text-xs text-red-600 mt-1">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.description}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-gradient text-white hover:opacity-90 py-5 text-lg font-bold shadow-lg mt-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-3">
              By submitting, you agree to our Terms & Privacy Policy
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
